import React, { useReducer } from 'react';
import assign from 'lodash/assign';
import * as Yup from 'yup';
import { Formik, Form } from 'formik';
import { useLazyQuery } from '@apollo/client';
import { useAuth } from '@guildeducationinc/guild-auth';
import { Button, Loading } from '@guildeducationinc/recess';
import { SEND_REFERRAL_EMAIL } from '@src/graphql/home/queries/sendReferralEmail';
import { clickable } from '@src/analytics';
import TextInputField from '@src/components/Shared/FormInputFields/TextInputField/TextInputField';
import { Server } from '@src/components/GqlProvider/GqlProvider';
import useEmployerService from '@src/hooks/useEmployerService';
import SuccessCard from '../Refer/SuccessCard/SuccessCard';

interface ReferralFormProps {
  label?: string;
}

interface ReferralValues {
  email: string;
}

interface State {
  errors?: string;
  success?: boolean;
  recipientEmail?: string;
}

type Action = { type: 'reset' } | { type: 'changeState'; payload: State };

const initialState: State = { errors: '', recipientEmail: '', success: false };

function reducer(state: State, action: Action): State {
  if (action.type === 'reset') {
    return initialState;
  } else {
    return assign({}, state, action.payload);
  }
}

const validationSchema = Yup.object({
  email: Yup.string()
    .email('Please enter one valid email at a time.')
    .required('Please enter an email address.'),
});

const ReferralForm: React.FC<ReferralFormProps> = ({ label }) => {
  const { user } = useAuth();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [sendReferralEmail, { data, error, loading }] = useLazyQuery(SEND_REFERRAL_EMAIL, {
    context: {
      server: Server.GLOBAL_APP_SYNC,
    },
  });

  const { employer } = useEmployerService(user?.employerId);

  const resetHandler = () => {
    dispatch({ type: 'reset' });
  };

  const onSubmit = (values: ReferralValues) => {
    dispatch({ type: 'reset' });

    sendReferralEmail({
      variables: {
        recipientEmail: values.email,
        referrerInfo: {
          referrerName: user?.firstName,
          referrerEmail: user?.email,
        },
        employerInfo: {
          employer: employer?.name,
          employerUuid: user?.employerId,
          employerNickname: employer?.subdomain,
        },
      },
    });

    dispatch({ type: 'changeState', payload: { recipientEmail: values.email } });
  };
  const formSubmitted = !state.success && state.recipientEmail;

  const commonClickableProps = {
    loggedIn: true,
    product: 'home',
    text: 'submit',
    type: 'button',
  };

  // Handles the case where the API request was successful; Lambda may or may not have encountered an error
  if (formSubmitted && data) {
    const status = data.tmpSoarEmails_createEmailEvent.status;
    // status here is *not* the api request status, it is from the return value of the lambda. 200 is a hard coded value in the lambda
    if (status !== '200') {
      dispatch({
        type: 'changeState',
        payload: { errors: 'An error occurred. Please try again later.', recipientEmail: '' },
      });
      clickable({ ...commonClickableProps, tag: 'referral_unsuccessful_submit' });
    } else {
      dispatch({ type: 'changeState', payload: { success: true } });
      clickable({ ...commonClickableProps, tag: 'referral_successful_submit' });
    }
  }

  // Handles the case where the API request was not successful
  if (formSubmitted && error) {
    dispatch({
      type: 'changeState',
      payload: { errors: 'An error occurred. Please try again later.', recipientEmail: '' },
    });
    clickable({ ...commonClickableProps, tag: 'referral_unsuccessful_submit' });
  }

  if (state.success && state.recipientEmail) {
    return <SuccessCard email={state.recipientEmail} handleReset={resetHandler} />;
  } else {
    return (
      <Formik
        initialValues={{
          email: '',
        }}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ handleBlur, handleChange, handleSubmit, values }) => (
          <Form>
            <TextInputField
              label={label || 'email input field'}
              required={true}
              name='email'
              placeholder='Email'
              onBlur={handleBlur}
              onChange={handleChange}
              value={values.email}
              errors={state.errors}
            />
            {formSubmitted && loading ? (
              <div className='referralLoadingSpinnerContainer margin--top_16 flex flexJustify--center'>
                <Loading label='form submission' />
              </div>
            ) : (
              <Button
                className='margin--top_16'
                testid='submitReferralButton'
                type='button'
                disabled={!employer}
                onClick={() => handleSubmit()}
              >
                Send referral
              </Button>
            )}
          </Form>
        )}
      </Formik>
    );
  }
};

export default ReferralForm;
